import React, { useState, useRef } from "react";
import { useFormDataContext } from "components/ContactForm/FormDataContext"
import { referralSourceOptions, serviceCounties } from "data"
import { useTurnstile, TURNSTILE_SITE_KEY, LOCALHOST_SITE_KEY} from "hooks/useTurnstile"
import { useWebsiteControls } from "context/WebsiteControlsContext"
import { Turnstile } from '@marsidev/react-turnstile'
import { Button, Input, Textarea, Select } from "components/controls"
import Card from 'components/Card/Card';
import axios from "axios";
import { getBaseUrl } from "connection/http";
import { faArrowLeft, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import './ContactForm.css';
import goatLogo from 'assets/graphics/goat.svg';

type FormValues = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	address: string;
	county: string;
	details: string;
}

type FormErrors = Partial<Record<keyof FormValues, string>>



function ContactForm() {
	const siteKey = process.env.NODE_ENV === 'production' ? TURNSTILE_SITE_KEY : LOCALHOST_SITE_KEY;

	const { websiteControls, loading: controlsLoading, error: controlsError } = useWebsiteControls();

	const isFormDisabled = websiteControls?.contactFormControls?.closeFormUntil ? new Date() < new Date(websiteControls.contactFormControls.closeFormUntil) : false;
	const formClosedHeader = websiteControls?.contactFormControls?.formClosedHeader || "Contact Form Closed";
	const formClosedMessage = websiteControls?.contactFormControls?.formClosedMessage || "The contact form is currently closed. Please try again later.";

	// Disable the form and show a message until Feb 1, 2027
	// const isFormDisabled = new Date() < new Date('2027-02-01T00:00:00');

	const formRef = useRef<HTMLFormElement>(null);
	const [sendLoading, setSendLoading] = useState(false)
	const [sendSuccessful, setSendSuccessful] = useState(false)
	const [sendFail, setSendFailed] = useState(false)
	const [validationErrors, setValidationErrors] = useState<FormErrors>({})

	const {
		address,
		setAddress,
		county,
		setCounty,
		firstName,
		setFirstName,
		lastName,
		setLastName,
		email,
		setEmail,
		phone,
		setPhone,
		jobDetails,
		setJobDetails,
		referralSource,
		setReferralSource,
		referralSourceOther,
		setReferralSourceOther,
		subscribe,
		setSubscribe,
		otherDetails,
		setOtherDetails
	} = useFormDataContext();

	const onFailBackClicked = () => {
		setSendFailed(false)
	}

	const validate = (): FormErrors => {
		const errors: FormErrors = {}

		if (!firstName?.trim()) {
			errors.firstName = "Please enter your first name"
		}

		if (!lastName?.trim()) {
			errors.lastName = "Please enter your last name"
		}

		if (!email?.trim()) {
			errors.email = "Please enter your email"
		}

		if (!phone?.trim()) {
			errors.phone = "Please enter your phone number"
		}

		if (!address?.trim()) {
			errors.address = "Please enter the service address"
		}

		if (!county?.trim()) {
			errors.county = "Please select the service county"
		}

		return errors
	}

	function handleBlur(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
		const { name } = e.target;

		const validationErrors = validate();

		setValidationErrors((prev) => ({
			...prev,
			[name]: validationErrors[name as keyof FormValues],
		}));
	}

	const hasErrors = (errors: FormErrors) => {
		return Object.keys(errors).length > 0;
	}

	const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const errors = validate()
		setValidationErrors(errors)

		if (hasErrors(errors)) {
			return; // stop — do NOT call server
		}
		submitRequest()
	}

	const submitRequest = async () => {
		setSendLoading(true)

		const formData = new FormData(formRef.current!)
    	const token = formData.get('cf-turnstile-response')

		const request = {
			token: token,
			address: address,
			county: county,
			firstName: firstName,
			lastName: lastName,
			email: email,
			phone: phone,
			referralSource: referralSource,
			referralSourceOther: referralSourceOther,
			subscribe: subscribe,
			jobDetails: jobDetails,
		}

		const headers = {
			"Content-type": "application/json"
		}
		axios.post(getBaseUrl() + "/newgoatlead", request, {
			headers: headers
		}).then(_ => {
			setSendSuccessful(true)
		}).catch(err =>  {
			setSendFailed(true)
			console.error(err)
		}).finally(() => {
			setSendLoading(false)
		})
	}

	const getPhoneScramble = () => {
		return atob('KDIwOCkgNDE5LTkyMzU=')
	}

	const getPhoneLinkScramble = () => {
		return atob('dGVsOisxMjA4NDE5OTIzNQ==')
	}

	const getEmailScramble = () => {
		return atob('dGhlZ29hdGdyYXplcnMudG5AZ21haWwuY29t')
	}

	const getMailLink = () => {
		return atob('bWFpbHRvOnRoZWdvYXRncmF6ZXJzLnRuQGdtYWlsLmNvbQ')
	}

	var body
	if (controlsLoading || (!websiteControls && !controlsError)) {
		body = (
			<div className="flex flex-col py-10">
				<p>Loading...</p>
			</div>
		)
	} else if (controlsError) {
		console.log(controlsError)
		body = (
			<div className="flex flex-col py-10">
				<p>Uh oh... something went wrong.</p>
				<p>We appologize for the inconvenience, but we were not able to load the contact form.</p>
				<p>Please send us an email or give us a call.</p>
				<p><a href={getPhoneLinkScramble()}>{getPhoneScramble()}</a></p>
				<p><a href={getMailLink()}>{getEmailScramble()}</a></p>
			</div>
		)
	}
	else if (sendLoading) {
		body = (
			<div className="flex flex-col py-10">
				<p>Give us a moment while we process your request.</p>
				{/* <Spinner color="primary" label="Give us a moment while we send that request for you." size="lg" /> */}
			</div>
		)
	} else if (sendSuccessful) {
		body = (
			<div className="flex flex-col items-center gap-3 py-10">
				<p>Thank you for requesting an estimate. We will get back to you as soon as we can.</p>
				<p>We look forward to working with you!</p>
			</div>
		)
	} else if (sendFail) {
		body = (
			<>
				<div className="">
					<h3>Uh oh... something went wrong.</h3>
					<p>We appologize for the inconvenience, but we were not able to send your estimate request.</p>
					<p>Please send us an email or give us a call.</p>
					<p><a href={getPhoneLinkScramble()}>{getPhoneScramble()}</a></p>
					<p><a href={getMailLink()}>{getEmailScramble()}</a></p>
				</div>
				<div className="">
					<Button 
						type="submit"
						variant="primary"
						icon={faArrowLeft}
						onClick={onFailBackClicked} >
						Back
					</Button>
				</div>
			</>
		)
	} else if (isFormDisabled) {
		body = (
			<div className="contact-form__disabled-message">
				<img src={goatLogo} alt="The G.O.A.T. Grazers goat logo" className="contact-form__logo" />
				<h3>{formClosedHeader}</h3>
				{formClosedMessage.split("\n").map((line, index) => (
					<p key={index}>{line}</p>
				))}
			</div>
		)
	} else {
		body = ( 
			<form onSubmit={submitForm} ref={formRef} noValidate>
				<div className="contact-form">
					<div className="contact-form-title">Let's get grazing! Contact us for an estimate.</div>
					<div className="form-row">
						<Input 
							id="firstName"
							label="First Name"
							isRequired
							errorMessage={validationErrors.firstName}
							value={firstName}
							handleBlur={handleBlur}
							onChange={setFirstName} />
						<Input 
							id="lastName"
							label="Last Name"
							isRequired
							errorMessage={validationErrors.lastName}
							value={lastName}
							handleBlur={handleBlur}
							onChange={setLastName} />
					</div>
					<Input 
						id="email"
						label="Email"
						isRequired
						errorMessage={validationErrors.email}
						value={email}
						handleBlur={handleBlur}
						onChange={setEmail} />
					<Input 
						id="phone"
						label="Phone"
						isRequired
						errorMessage={validationErrors.phone}
						value={phone}
						handleBlur={handleBlur}
						onChange={setPhone} />
					<Input 
						id="address"
						label="Address"
						isRequired
						errorMessage={validationErrors.address}
						value={address} 
						handleBlur={handleBlur}
						onChange={setAddress} />
					<Select
						id="county"
						label="County"
						isRequired
						errorMessage={validationErrors.county}
						value={county}
						options={serviceCounties}
						handleBlur={handleBlur}
						onChange={setCounty} />
					<Textarea
						id="details"
						label="More Details"
						description="Let us know any details that we should know about the job site such as: Approximate area of the site, type of vegetation, type of terrain (flat, steep, or rocky), any obstacles (creeks, ditches, fallen trees, debris), parking availability, accessibility for truck and trailer, water availability near the site."
						value={jobDetails}
						onChange={setJobDetails} />
					<Turnstile siteKey={siteKey} />
					<Button 
						type="submit"
						variant="primary"
						icon={faPaperPlane} >
						Submit Request
					</Button>
				</div>
			</form>
		)
	}

	return (
		<Card variant="green-outlined" hoverable={false} className="contact-form-container">
			{body}
		</Card>
	);
}

export default ContactForm;